import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SupplierPaymentReportDisplay = ({ formData, filters, branchProfile, paymentData }) => {
  const parseVoucherDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return new Date(dateString);
    }
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    return new Date(dateString);
  };

  const formatDisplayDate = (dateString) => {
    const date = parseVoucherDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Deduplicate payment data by MRN number for accurate totals
  const deduplicatedPaymentData = Object.values(
    paymentData.reduce((acc, item) => {
      const mrnNo = item.mrnNo || 'no-mrn-' + item.id; // Use transaction ID as fallback if no MRN
      if (!acc[mrnNo]) {
        acc[mrnNo] = {
          ...item,
          paymentAmount: parseFloat(item.paymentAmount) || 0,
          totalAmount: parseFloat(item.totalAmount) || 0,
          netAmount: parseFloat(item.netAmount) || 0,
          paymentCount: 1
        };
        // Recalculate netAmount to ensure consistency
        acc[mrnNo].netAmount = acc[mrnNo].totalAmount - acc[mrnNo].paymentAmount;
      } else {
        // Aggregate payments for the same MRN
        acc[mrnNo].paymentAmount += parseFloat(item.paymentAmount) || 0;
        acc[mrnNo].netAmount = acc[mrnNo].totalAmount - acc[mrnNo].paymentAmount; // Recalculate outstanding
        acc[mrnNo].paymentCount += 1;
      }
      // Ensure netAmount is never negative (outstanding cannot be less than 0)
      acc[mrnNo].netAmount = Math.max(0, acc[mrnNo].netAmount);
      return acc;
    }, {})
  );

  const filteredPaymentData = deduplicatedPaymentData.filter(item => {
    const itemDate = parseVoucherDate(item.voucherDate);
    const fromDate = formData.fromDate ? new Date(formData.fromDate) : null;
    const toDate = formData.toDate ? new Date(formData.toDate) : null;
    toDate?.setHours(23, 59, 59, 999);

    let matchesFilters = true;
    if (fromDate) matchesFilters = matchesFilters && itemDate >= fromDate;
    if (toDate) matchesFilters = matchesFilters && itemDate <= toDate;
    if (filters.supplier.length > 0 && !filters.supplier.includes("All Suppliers")) {
      matchesFilters = matchesFilters && filters.supplier.includes(item.supplier || "Unknown Supplier");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    return matchesFilters;
  });

  const aggregatedData = formData.reportType === 'abstract'
    ? filteredPaymentData.reduce((acc, item) => {
        const key = item.supplier;
        if (!acc[key]) {
          acc[key] = {
            supplier: item.supplier || 'Unknown Supplier',
            totalAmount: 0,
            paymentAmount: 0,
            outstanding: 0
          };
        }
        acc[key].totalAmount += parseFloat(item.totalAmount) || 0;
        acc[key].paymentAmount += parseFloat(item.paymentAmount) || 0;
        acc[key].outstanding += parseFloat(item.netAmount) || 0;
        return acc;
      }, {})
    : filteredPaymentData.map((item, index) => ({
        ...item,
        sno: index + 1
      }));

  const sortedData = formData.reportType === 'abstract'
    ? Object.values(aggregatedData).sort((a, b) => a.supplier.localeCompare(b.supplier))
    : filteredPaymentData.sort((a, b) => {
        const dateA = parseVoucherDate(a.voucherDate);
        const dateB = parseVoucherDate(b.voucherDate);
        return dateA - dateB;
      });

  // Calculate grand totals
  const totalAmount = sortedData.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
  const totalPayment = sortedData.reduce((sum, item) => sum + (parseFloat(item.paymentAmount) || 0), 0);
  const totalOutstanding = sortedData.reduce((sum, item) => sum + (parseFloat(formData.reportType === 'abstract' ? item.outstanding : item.netAmount) || 0), 0);

  const reportTitle = 'Supplier Payment Report';
  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {reportTitle}
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {formData.fromDate ? formatDisplayDate(formData.fromDate) : 'All'}</span>
              <span><strong>To:</strong> {formData.toDate ? formatDisplayDate(formData.toDate) : 'All'}</span>
            </div>
            <div style={{ marginTop: '8px' }}>
              <strong>Suppliers:</strong> {filters.supplier.includes("All Suppliers") ? "All Suppliers" : filters.supplier.join(", ")}
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
          
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'abstract' ? 'Abstract' : 'Billwise'}
          </div>
        </div>
      </div>

      {/* Payment Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
              {formData.reportType === 'abstract' ? (
                <>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total Amount</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Payment Amount</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Outstanding</th>
                </>
              ) : (
                <>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Voucher No</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRN No</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Payment By</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total Amount</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Payment Amount</th>
                  <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Outstanding</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={formData.reportType === 'abstract' ? 5 : 9} style={{ 
                  border: '1px solid #cbd5e1', 
                  padding: '30px', 
                  textAlign: 'center', 
                  color: '#64748b', 
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: '#f8fafc'
                }}>
                  No payment data found for the selected criteria
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                  {formData.reportType === 'abstract' ? (
                    <>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.totalAmount.toFixed(2)}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.paymentAmount.toFixed(2)}</td>
                      <td style={{ 
                        border: '1px solid #cbd5e1', 
                        padding: '8px', 
                        textAlign: 'right', 
                        fontSize: '11px',
                        color: item.outstanding > 0 ? '#dc2626' : '#059669',
                        fontWeight: item.outstanding > 0 ? '500' : '400'
                      }}>
                        ₹{item.outstanding.toFixed(2)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.voucherNo || '-'}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.voucherDate)}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier || 'Unknown Supplier'}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                        {item.mrnNo ? item.mrnNo : 'N/A'}
                      </td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.paymentBy || '-'}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{parseFloat(item.totalAmount || 0).toFixed(2)}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{parseFloat(item.paymentAmount || 0).toFixed(2)}</td>
                      <td style={{ 
                        border: '1px solid #cbd5e1', 
                        padding: '8px', 
                        textAlign: 'right', 
                        fontSize: '11px',
                        color: parseFloat(item.netAmount || 0) > 0 ? '#dc2626' : '#059669',
                        fontWeight: parseFloat(item.netAmount || 0) > 0 ? '500' : '400'
                      }}>
                        ₹{parseFloat(item.netAmount || 0).toFixed(2)}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
            
            {sortedData.length > 0 && (
              <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                <td colSpan={formData.reportType === 'abstract' ? 2 : 6} style={{ 
                  border: '1px solid #cbd5e1', 
                  padding: '10px', 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  fontWeight: '600' 
                }}>
                  Grand Total
                </td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalAmount.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalPayment.toFixed(2)}</td>
                <td style={{ 
                  border: '1px solid #cbd5e1', 
                  padding: '10px', 
                  textAlign: 'right', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  color: totalOutstanding > 0 ? '#dc2626' : '#059669'
                }}>
                  ₹{totalOutstanding.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {sortedData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{sortedData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Payment</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>₹{totalPayment.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Outstanding</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalOutstanding.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Main Supplier Payment Entry Report Generator Component
const SupplierPaymentEntryReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [reportType, setReportType] = useState("billwise");
  const [filters, setFilters] = useState({
    branch: ["All Branches"],
    supplier: ["All Suppliers"]
  });
  const [branches, setBranches] = useState(["All Branches"]);
  const [suppliers, setSuppliers] = useState(["All Suppliers"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [paymentData, setPaymentData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
        setBranches(["All Branches", latestProfile.branchName || "SUPREME TRADERS"]);
      }
    });

    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Suppliers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
          }
        });
      }
      setSuppliers(suppliersData);
    });

    // Load invoices to get MRN numbers
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    onValue(invoicesRef, (invoicesSnapshot) => {
      const invoicesData = invoicesSnapshot.val() || {};
      
      const transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
      onValue(transactionsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const paymentRecords = Object.entries(data).map(([id, transaction]) => {
            // Get MRN number from transaction or from related transactions array
            let mrnNo = transaction.mrnNo || '';
            
            // If MRN number is not available in transaction, try to get it from transactions array
            if (!mrnNo && transaction.transactions && transaction.transactions.length > 0) {
              // Get MRN from the first transaction that has it
              const transactionWithMrn = transaction.transactions.find(t => t.mrnNo);
              if (transactionWithMrn) {
                mrnNo = transactionWithMrn.mrnNo;
              }
            }
            
            // If still no MRN number, try to get it from related invoices
            if (!mrnNo && transaction.transactions && transaction.transactions.length > 0) {
              const invoiceIds = transaction.transactions
                .map(t => t.invoiceId)
                .filter(Boolean);
              
              for (const invoiceId of invoiceIds) {
                const invoice = invoicesData[invoiceId];
                if (invoice && invoice.supplierDetails && invoice.supplierDetails.mrnNo) {
                  mrnNo = invoice.supplierDetails.mrnNo;
                  break;
                }
              }
            }
            
            // Debug logging to help identify MRN number issues
            if (!mrnNo) {
              console.log('MRN number missing for transaction:', {
                id,
                supplier: transaction.supplier,
                voucherNo: transaction.voucherNo,
                hasMrnNo: !!transaction.mrnNo,
                hasTransactions: !!transaction.transactions,
                transactionsLength: transaction.transactions?.length || 0,
                invoiceIds: transaction.transactions?.map(t => t.invoiceId).filter(Boolean) || []
              });
            }
            
            return {
              id,
              voucherNo: transaction.voucherNo || '',
              voucherDate: transaction.voucherDate || '',
              supplier: transaction.supplier || 'Unknown Supplier',
              supplierAddress: transaction.supplierAddress || '',
              supplierCity: transaction.supplierCity || '',
              mrnNo: mrnNo,
              paymentBy: transaction.paymentBy || '',
              totalAmount: parseFloat(transaction.totalAmount) || 0,
              paymentAmount: parseFloat(transaction.paymentAmount) || 0,
              netAmount: parseFloat(transaction.netAmount) || 0,
              remarks: transaction.remarks || ''
            };
          });
          setPaymentData(paymentRecords);
        } else {
          setPaymentData([]);
        }
      });
    });

    return () => {
      off(branchProfileRef);
      off(suppliersRef);
      off(invoicesRef);
      // Note: transactionsRef is cleaned up inside the invoicesRef callback
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

 const handlePrint = () => {
  const printContent = (
    <SupplierPaymentReportDisplay 
      formData={{ fromDate, toDate, reportType }}
      filters={filters}
      branchProfile={branchProfile}
      paymentData={paymentData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Supplier Payment Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
                padding: 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          /* Specific column widths for better fit */
          th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
          th:nth-child(2), td:nth-child(2) { width: 80px; } /* Voucher No or Supplier */
          th:nth-child(3), td:nth-child(3) { width: 60px; } /* Date or Total Amount */
          th:nth-child(4), td:nth-child(4) { width: 80px; } /* Supplier or Payment Amount */
          th:nth-child(5), td:nth-child(5) { width: 80px; } /* MRN No or Outstanding */
          th:nth-child(6), td:nth-child(6) { width: 80px; } /* Payment By */
          th:nth-child(7), td:nth-child(7) { width: 80px; } /* Total Amount */
          th:nth-child(8), td:nth-child(8) { width: 80px; } /* Payment Amount */
          th:nth-child(9), td:nth-child(9) { width: 80px; font-weight: bold; } /* Outstanding */
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Payment Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Supplier Payment Entry Report </p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Supplier Payment Entry Report </h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="billwise">Billwise</option>
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        whiteSpace: 'nowrap'
      }}>
        <FilterSection 
          title="Branches" 
          options={branches} 
          selected={filters.branch} 
          onChange={(value) => handleFilterChange("branch", value)}
        />
        <FilterSection 
          title="Suppliers" 
          options={suppliers} 
          selected={filters.supplier} 
          onChange={(value) => handleFilterChange("supplier", value)}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Supplier Payment Report Display */}
      {showReport && (
        <SupplierPaymentReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          paymentData={paymentData}
        />
      )}
    </div>
  );
};

export default SupplierPaymentEntryReportGenerator;